import {DurationType, IssueGroupingKey} from '@domain/stoppages'
import {useQueryParamValue} from '@hconnect/common/hooks'
import {EquipmentData, StoppageKind} from '@hconnect/common/types'
import {setOrDeleteArrayParam, URL_PARAM_ARRAY_SEPARATOR} from '@hconnect/common/utils'
import {useState} from 'react'
import {useSearchParams} from 'react-router'

import {FAILURE_STOPPAGE, STOPPAGE_DURATION} from '../consts'
import {
  getDurationTypesFromUrl,
  getEquipmentsFromUrl,
  getFailureKindsFromUrl,
  getStoppageCodesFromUrl
} from '../utils'

type UseDowntimeFiltersStateOptions = {
  groupType: IssueGroupingKey
  groupId: string
}

const useFailureStoppageState = () => {
  const [failureStoppageParam, setFailureStoppageParam] = useQueryParamValue<string>(
    FAILURE_STOPPAGE,
    {
      keepInLocalStorage: true
    }
  )

  const failureStoppage = getFailureKindsFromUrl(failureStoppageParam)

  const handleFailureStoppageChange = (value?: StoppageKind[]) => {
    const paramValue = value?.length ? value.join(URL_PARAM_ARRAY_SEPARATOR) : undefined
    setFailureStoppageParam(paramValue)
  }

  return {
    failureStoppage,
    setFailureStoppage: handleFailureStoppageChange
  }
}

const useStoppageDurationState = () => {
  const [stoppageDurationParam, setStoppageDurationParam] = useQueryParamValue<string>(
    STOPPAGE_DURATION,
    {
      keepInLocalStorage: true
    }
  )

  const stoppageDuration = getDurationTypesFromUrl(stoppageDurationParam)

  const handleStoppageDurationChange = (value?: DurationType[]) => {
    const paramValue = value?.length ? value.join(URL_PARAM_ARRAY_SEPARATOR) : undefined
    setStoppageDurationParam(paramValue)
  }

  return {
    stoppageDuration,
    setStoppageDuration: handleStoppageDurationChange
  }
}

const useMainEquipmentsState = (options?: UseDowntimeFiltersStateOptions) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [mainEquipments, setMainEquipments] = useState<EquipmentData[] | undefined>(
    getEquipmentsFromUrl(
      options?.groupType === IssueGroupingKey.MAIN_EQUIPMENT
        ? options.groupId
        : searchParams.get('mainEquipmentNumbers')
    )
  )

  const handleMainEquipmentsChange = (value?: EquipmentData[]) => {
    setMainEquipments(value)
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      setOrDeleteArrayParam(
        newParams,
        'mainEquipmentNumbers',
        value?.map((eq) => eq.id)
      )
      return newParams
    })
  }

  return {
    mainEquipments,
    setMainEquipments: handleMainEquipmentsChange
  }
}

const useEquipmentNumbersState = (options?: UseDowntimeFiltersStateOptions) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [equipmentNumbers, setEquipmentNumbers] = useState<EquipmentData[] | undefined>(
    getEquipmentsFromUrl(
      options?.groupType === IssueGroupingKey.EQUIPMENT_NUMBER
        ? options.groupId
        : searchParams.get('equipmentNumbers')
    )
  )

  const handleEquipmentNumbersChange = (value?: EquipmentData[]) => {
    setEquipmentNumbers(value)
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      setOrDeleteArrayParam(
        newParams,
        'equipmentNumbers',
        value?.map((eq) => eq.id)
      )
      return newParams
    })
  }

  return {
    equipmentNumbers,
    setEquipmentNumbers: handleEquipmentNumbersChange
  }
}

const useStoppageCodesState = (options?: UseDowntimeFiltersStateOptions) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [stoppageCodes, setStoppageCodes] = useState<string[] | undefined>(
    getStoppageCodesFromUrl(
      options?.groupType === IssueGroupingKey.STOPPAGE_CODE
        ? options.groupId
        : searchParams.get('stoppageCode')
    )
  )

  const handleStoppageCodesChange = (value?: string[]) => {
    setStoppageCodes(value)
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      setOrDeleteArrayParam(newParams, 'stoppageCodes', value)
      return newParams
    })
  }

  return {
    stoppageCodes,
    setStoppageCodes: handleStoppageCodesChange
  }
}

type UseDowntimeFiltersStateReturn = {
  failureStoppage?: StoppageKind[]
  setFailureStoppage: (value?: StoppageKind[]) => void
  mainEquipments?: EquipmentData[]
  setMainEquipments: (value?: EquipmentData[]) => void
  equipmentNumbers?: EquipmentData[]
  setEquipmentNumbers: (value?: EquipmentData[]) => void
  stoppageCodes?: string[]
  setStoppageCodes: (value?: string[]) => void
  stoppageDuration?: DurationType[]
  setStoppageDuration: (value?: DurationType[]) => void
}

export const useDowntimeFiltersState = (
  options?: UseDowntimeFiltersStateOptions
): UseDowntimeFiltersStateReturn => {
  const failureStoppageState = useFailureStoppageState()
  const stoppageDurationState = useStoppageDurationState()
  const mainEquipmentsState = useMainEquipmentsState(options)
  const equipmentNumbersState = useEquipmentNumbersState(options)
  const stoppageCodesState = useStoppageCodesState(options)

  return {
    ...failureStoppageState,
    ...stoppageDurationState,
    ...mainEquipmentsState,
    ...equipmentNumbersState,
    ...stoppageCodesState
  }
}
